import { apiClient } from '@/api/api'
import type { SavedCardAvailabilityStatus } from '@/features/favorites/api/favorites'
import type { SavedProductCard } from '@/features/favorites/api/favorites'
import type { WishlistsResponse } from '@/types/WishlistTypes'

export const getWishlists = () =>
  apiClient.get<WishlistsResponse>('wishlist/lists')

export const createWishlist = (name: string) =>
  apiClient.post<{ id: number; name: string }>('wishlist/lists', { name })

export const addToWishlist = (wishlistId: number, productVariantId: number) =>
  apiClient.post(`wishlist/${wishlistId}/items`, { productVariantId })

export const removeFromWishlist = (wishlistId: number, variantId: number) =>
  apiClient.delete(`wishlist/${wishlistId}/items/${variantId}`)

export const deleteWishlist = (wishlistId: number) =>
  apiClient.delete(`wishlist/${wishlistId}`)

/** BE-123 — `PATCH wishlist/{id} { name }`. */
export const renameWishlist = (wishlistId: number, name: string) =>
  apiClient.patch<{ id: number; name: string }>(`wishlist/${wishlistId}`, { name })

/**
 * `SavedProductCard` as `wishlist/{id}/items` delivers it: the path is NOT on
 * the interceptor's exclusion list (unlike `favorites/products`), so the same
 * `SavedProductCardEntity` arrives camelised. Declared on purpose rather than
 * excluding `wishlist/` — the lists page already reads `itemsCount`.
 */
export interface WishlistSavedProductCard {
  id: number
  name: string
  brand: string
  price: { gte: number | null; lte: number | null }
  currency?: string | null
  imageUrls: string[]
  isFavorite: boolean
  productId: number
  variantId: number
  /** BE-008 — the same three facts `/favorites/products` carries, camelised on this path. */
  addedAt?: string | null
  priceWithoutDiscount?: number | null
  percentageOfDiscount?: number | null
  availability?: { status: SavedCardAvailabilityStatus; savedSizeAvailable: boolean } | null
}

/** `GET /wishlist/{wishlist_id}/items` (BE-121, snapshot `get_api_v0_wishlist_wishlist_id_items.json`). */
export interface WishlistItemsResponse {
  items: WishlistSavedProductCard[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export interface WishlistItemsParams {
  page?: number
  page_size?: number
}

export const getWishlistItems = (
  wishlistId: number,
  { page = 1, page_size = 20 }: WishlistItemsParams = {},
) =>
  apiClient.get<WishlistItemsResponse>(`wishlist/${wishlistId}/items`, {
    params: { page, page_size },
  })

export type { SavedProductCard }
